import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import {
  generateShareLink,
  generateAIShareType,
  addAIShareType,
  getUserShareTypes,
  deleteUserShareType,
  getAILimitStatus,
} from '../../services/orderService';
import type {
  ShareOrderRequest,
  AddAIShareTypeRequest,
  UserShareType,
  AILimitStatusResponse,
} from '../../services/orderService';
import { useAuth } from '../../hooks/useAuth';
import { moderateTextWithContext } from '../../services/moderationService';
import AILogo from '../../assets/img/ai.svg';
import { HugeiconsIcon } from '@hugeicons/react';
import { AiEditingIcon } from '@hugeicons/core-free-icons';
import { isMobile } from 'react-device-detect';

// Theme configuration interface
interface ThemeConfig {
  icon: string;
  bgGradient: string;
  cardGradient: string;
  accentColor: string;
  textColor: string;
  particles: string[];
}

// Animated particles component
const AnimatedParticles: React.FC<{ particles: string[]; theme: ThemeConfig }> = ({
  particles,
}) => {
  const [animatedParticles, setAnimatedParticles] = useState<
    Array<{ id: number; emoji: string; x: number; y: number; delay: number; duration: number }>
  >([]);

  useEffect(() => {
    // Create random positioned particles like SharePage
    const newParticles = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      emoji: particles[Math.floor(Math.random() * particles.length)],
      x: Math.random() * 90 + 5, // 5% to 95% to avoid edges
      y: Math.random() * 80 + 10, // 10% to 90% to avoid edges
      delay: Math.random() * 2, // Random delay 0-2s
      duration: 1.5 + Math.random() * 1, // Random duration 1.5-2.5s
    }));
    setAnimatedParticles(newParticles);
  }, [particles]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {animatedParticles.map((particle) => (
        <div
          key={particle.id}
          className="absolute text-xl animate-ping opacity-60"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`,
          }}
        >
          {particle.emoji}
        </div>
      ))}
    </div>
  );
};

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderStatus?: string;
  onSuccess: (shareUrl: string, shareType: string, note?: string) => void;
}

const shareTypes = [
  { value: 'Gift', icon: '🎁', color: 'bg-pink-500' },
  { value: 'Treat', icon: '🍰', color: 'bg-orange-500' },
  { value: 'Share', icon: '🤝', color: 'bg-blue-500' },
  { value: 'Allowance', icon: '💰', color: 'bg-green-500' },
  { value: 'Repayment', icon: '💳', color: 'bg-purple-500' },
  { value: 'Love', icon: '❤️', color: 'bg-red-500' },
] as const;

export const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  onClose,
  orderId,
  orderStatus,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [selectedType, setSelectedType] = useState<string>('Gift');
  const [note, setNote] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidatingNote, setIsValidatingNote] = useState(false);
  const [noteValidationError, setNoteValidationError] = useState<string | null>(null);
  const [detectedProfanityWords, setDetectedProfanityWords] = useState<string[]>([]);
  const [validationTimeout, setValidationTimeout] = useState<NodeJS.Timeout | null>(null);

  // AI Generation states
  const [isAiMode, setIsAiMode] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiGeneratedType, setAiGeneratedType] = useState<{
    title: string;
    icon: string;
    bgGradient: string;
    cardGradient: string;
    accentColor: string;
    textColor: string;
    particles: string[];
  } | null>(null);

  // User created share types
  const [userShareTypes, setUserShareTypes] = useState<UserShareType[]>([]);
  const [isLoadingUserTypes, setIsLoadingUserTypes] = useState(false);
  const [deletingTypeId, setDeletingTypeId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<string | null>(null);
  const [isReminderForAi, setIsReminderForAi] = useState(false);

  // AI Limit Status
  const [aiLimitStatus, setAiLimitStatus] = useState<AILimitStatusResponse['data'] | null>(null);
  const [isLoadingAiLimit, setIsLoadingAiLimit] = useState(false);

  // Helper function to highlight profanity words in text
  const highlightProfanityWords = (text: string, profanityWords: string[]) => {
    if (!profanityWords.length) return text;

    let highlightedText = text;
    profanityWords.forEach((word) => {
      const regex = new RegExp(`(${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      highlightedText = highlightedText.replace(
        regex,
        '<span style="color: red; font-weight: bold;">$1</span>',
      );
    });

    return highlightedText;
  };

  const validateNote = useCallback(
    async (noteText: string) => {
      if (!noteText.trim()) {
        setNoteValidationError(null);
        return true;
      }

      setIsValidatingNote(true);
      setNoteValidationError(null);

      try {
        console.log('🔍 Testing profanity detection for:', noteText);
        const moderationResult = await moderateTextWithContext(noteText.trim(), {
          contentType: 'share_note',
          platform: 'myeztopup',
        });

        console.log('🔍 Moderation result:', moderationResult);

        if (!moderationResult.isAppropriate) {
          const errorKey = moderationResult.reason || 'share.inappropriate_content';
          setNoteValidationError(
            t(errorKey) || t('share.noteValidationFailed') || 'Note contains inappropriate content',
          );
          setDetectedProfanityWords(moderationResult.detectedWords || []);
          console.log('🚨 Inappropriate content detected:', moderationResult.detectedWords);
          return false;
        }

        setDetectedProfanityWords([]);
        console.log('✅ Content is appropriate');
        return true;
      } catch (error) {
        console.error('Note validation error:', error);
        // Don't block on validation errors, just log them
        return true;
      } finally {
        setIsValidatingNote(false);
      }
    },
    [t],
  );

  const debouncedValidateNote = useCallback(
    (noteText: string) => {
      // Clear existing timeout
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }

      // If note is empty, clear validation immediately
      if (!noteText.trim()) {
        setNoteValidationError(null);
        setDetectedProfanityWords([]);
        setIsValidatingNote(false);
        return;
      }

      // Set new timeout for validation
      const timeout = setTimeout(() => {
        validateNote(noteText);
      }, 300); // 300ms delay for faster real-time validation

      setValidationTimeout(timeout);
    },
    [validationTimeout, validateNote],
  );

  const handleGenerateShare = useCallback(async () => {
    if (!user) {
      setError(t('share.loginRequired') || 'You must be logged in to share orders');
      return;
    }

    // If there's a validation error, don't proceed
    if (noteValidationError) {
      return;
    }

    // Final validation before generating share link
    const isNoteValid = await validateNote(note);
    if (!isNoteValid) {
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Check if the selected type is a user share type
      const userShareType = userShareTypes.find((type) => type.title === selectedType);

      // Create request object
      const shareRequest: ShareOrderRequest = {
        note: note.trim() || undefined,
      };

      // If it's a user share type, add shareTypeId and don't set type
      if (userShareType) {
        shareRequest.shareTypeId = userShareType._id;
      } else {
        // If it's a default share type, set the type field
        shareRequest.type = selectedType as ShareOrderRequest['type'];
      }

      const response = await generateShareLink(orderId, shareRequest);
      if (response.success) {
        onSuccess(response.data.shareUrl, selectedType, note.trim() || undefined);
        onClose();
      } else {
        setError(response.message || t('share.generateError') || 'Failed to generate share link');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage || t('share.generateError') || 'Failed to generate share link');
    } finally {
      setIsGenerating(false);
    }
  }, [
    user,
    noteValidationError,
    validateNote,
    note,
    userShareTypes,
    selectedType,
    orderId,
    onSuccess,
    onClose,
    t,
  ]);

  // Fetch user created share types
  useEffect(() => {
    const fetchUserShareTypes = async () => {
      if (!user || !isOpen) return;

      setIsLoadingUserTypes(true);
      try {
        const response = await getUserShareTypes({ limit: 20 });
        if (response.success) {
          setUserShareTypes(response.data);
          // Auto select first user share type if no type is selected
          if (!selectedType && response.data.length > 0) {
            setSelectedType(response.data[0].title);
          } else if (!selectedType && response.data.length === 0) {
            // If no user share types, select first default share type
            setSelectedType('Gift');
          }
        }
      } catch (error) {
        console.error('Failed to fetch user share types:', error);
      } finally {
        setIsLoadingUserTypes(false);
      }
    };

    fetchUserShareTypes();
  }, [user, isOpen, selectedType]);

  const handleDeleteShareType = async () => {
    if (!typeToDelete) return;

    setDeletingTypeId(typeToDelete); // Set loading state when API call starts
    try {
      await deleteUserShareType(typeToDelete);
      // Remove the deleted share type from the list
      setUserShareTypes((prev) => prev.filter((type) => type._id !== typeToDelete));
      showToast('success', 'Share type deleted successfully');
    } catch (error) {
      console.error('Error deleting share type:', error);
      showToast('error', 'Failed to delete share type');
    } finally {
      setDeletingTypeId(null);
      setShowDeleteDialog(false);
      setTypeToDelete(null);
    }
  };

  const confirmDelete = (shareTypeId: string) => {
    setTypeToDelete(shareTypeId);
    setShowDeleteDialog(true);
  };

  // Fetch AI limit status
  const fetchAiLimitStatus = useCallback(async () => {
    if (!user) return;

    setIsLoadingAiLimit(true);
    try {
      const response = await getAILimitStatus();
      if (response.success) {
        setAiLimitStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch AI limit status:', error);
    } finally {
      setIsLoadingAiLimit(false);
    }
  }, [user]);

  // Fetch AI limit status on dialog open and after AI generation
  useEffect(() => {
    if (isOpen && user && isAiMode) {
      fetchAiLimitStatus();
    }
  }, [isOpen, user, isAiMode, fetchAiLimitStatus]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('share.title') || 'Share Order'}
          </h2>
          <p className="text-sm text-gray-600">
            {t('share.description') || 'Choose how you want to share this order with others'}
          </p>
        </div>

        {/* Failed/Cancelled Order Warning */}
        {(orderStatus === 'failed' || orderStatus === 'cancelled') && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <svg
                className="w-5 h-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <h3 className="text-sm font-medium text-red-800">
                {orderStatus === 'failed'
                  ? t('share.failedOrderTitle') || 'Cannot Share Failed Order'
                  : t('share.cancelledOrderTitle') || 'Cannot Share Cancelled Order'}
              </h3>
            </div>
            <p className="text-sm text-red-700">
              {orderStatus === 'failed'
                ? t('share.failedOrderMessage') ||
                  'This order has failed and cannot be shared. Only successful orders can be shared with others.'
                : t('share.cancelledOrderMessage') ||
                  'This order has been cancelled and cannot be shared. Only successful orders can be shared with others.'}
            </p>
          </div>
        )}

        {/* Share Type Selection */}
        {!isAiMode ? (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('share.selectType') || 'Select Share Type'}
            </label>
            <div className="overflow-x-auto py-4">
              <div className="flex space-x-3 min-w-max">
                {/* Create With AI Button */}
                <button
                  key="ai-button"
                  onClick={() => {
                    if (userShareTypes.length >= 5) {
                      setIsReminderForAi(true);
                    } else if (orderStatus !== 'failed' && userShareTypes.length <= 4) {
                      setIsAiMode(true);
                    }
                  }}
                  disabled={
                    orderStatus === 'failed' || orderStatus === 'cancelled' || isLoadingUserTypes
                  }
                  className={`flex-shrink-0 p-4 rounded-lg transition-all duration-200 min-w-[120px] ${
                    orderStatus === 'failed' || orderStatus === 'cancelled'
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                      : 'text-black border-2 border-grey bg-white shadow-md hover:shadow-lg relative before:absolute before:-inset-[2px] before:rounded-lg before:bg-gradient-to-r before:from-purple-400 before:to-pink-400 before:-z-10 before:content-[""] hover:before:from-purple-500 hover:before:to-pink-500'
                  }`}
                >
                  <div className="text-2xl mb-2">
                    <img src={AILogo} alt="AI" className="inline-block w-8 h-8" />
                  </div>
                  <div className="text-sm font-medium">Create With AI</div>
                </button>

                <div
                  key="separator-1"
                  className="flex-shrink-0 rounded-lg border-2 transition-all duration-200"
                ></div>

                {/* Loading indicator for user share types */}
                {isLoadingUserTypes && (
                  <div
                    key="loading-user-types"
                    className="flex-shrink-0 p-4 rounded-lg border-2 border-gray-200 min-w-[100px] flex items-center justify-center"
                  >
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                {/* User Created Share Types */}
                {userShareTypes?.map((userType) => (
                  <div key={userType._id} className="relative flex-shrink-0">
                    <button
                      onClick={() =>
                        orderStatus !== 'failed' &&
                        orderStatus !== 'cancelled' &&
                        setSelectedType(userType.title)
                      }
                      disabled={orderStatus === 'failed' || orderStatus === 'cancelled'}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 min-w-[100px] ${
                        orderStatus === 'failed' || orderStatus === 'cancelled'
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                          : selectedType === userType.title
                            ? `bg-gradient-to-r ${userType.cardGradient ? userType.cardGradient : 'from-green-400 to-teal-500'} text-white border-2 shadow-lg`
                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">{userType.icon}</div>
                      <div className="text-sm font-medium">{userType.title}</div>
                    </button>
                    <button
                      key={`delete-button-${userType._id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(userType._id);
                      }}
                      disabled={deletingTypeId === userType._id}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors duration-200 disabled:opacity-50"
                      title="Delete share type"
                    >
                      {deletingTypeId === userType._id ? (
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        '×'
                      )}
                    </button>
                  </div>
                ))}

                {userShareTypes.length > 0 && (
                  <div
                    key="separator-2"
                    className="flex-shrink-0 rounded-lg border-2 transition-all duration-200"
                  ></div>
                )}

                {/* Default Share Types */}
                {shareTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() =>
                      orderStatus !== 'failed' &&
                      orderStatus !== 'cancelled' &&
                      setSelectedType(type.value)
                    }
                    disabled={orderStatus === 'failed' || orderStatus === 'cancelled'}
                    className={`flex-shrink-0 p-4 rounded-lg border-2 transition-all duration-200 min-w-[100px] ${
                      orderStatus === 'failed' || orderStatus === 'cancelled'
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                        : selectedType === type.value
                          ? `${type.color} text-white border-transparent shadow-lg`
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-2">{type.icon}</div>
                    <div className="text-sm font-medium">
                      {t(`share.types.${type.value.toLowerCase()}`) || type.value}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6">
            {/* AI Generation Interface */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  <img src={AILogo} alt="AI" className="inline-block w-4 h-4 mr-2" />
                  Create Share Type with AI
                </label>

                {isLoadingAiLimit ? (
                  <div className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <span className="inline-flex">
                      {'Loading...'.split('').map((char, index) => (
                        <span
                          key={index}
                          className="animate-pulse"
                          style={{
                            animationDelay: `${index * 0.1}s`,
                            animationDuration: '1s',
                          }}
                        >
                          {char}
                        </span>
                      ))}
                    </span>
                  </div>
                ) : (
                  aiLimitStatus && (
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium transition-all duration-300 ${
                        aiLimitStatus.remainingCount > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      <span className="transition-all duration-500 hover:scale-110">
                        {aiLimitStatus.remainingCount} / {aiLimitStatus.dailyLimit} left
                      </span>
                    </div>
                  )
                )}

                {/* <button
                  onClick={() => {
                    setIsAiMode(false);
                    setAiPrompt('');
                    setAiGeneratedType(null);
                  }}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button> */}
              </div>

              <div className="flex space-x-2 mb-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={aiPrompt}
                    autoFocus={!isMobile}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Describe the share type you want to create..."
                    disabled={aiLimitStatus?.remainingCount === 0}
                    className={`w-full px-3 py-2 rounded-lg outline-none transition-all duration-300 ${
                      isGeneratingAi
                        ? 'animate-border-spin'
                        : 'border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                    }`}
                  />
                </div>
                <button
                  onClick={async () => {
                    if (!aiPrompt.trim()) return;

                    setIsGeneratingAi(true);
                    try {
                      // Call AI generation service using the service function
                      const response = await generateAIShareType({
                        userText:
                          aiPrompt.trim() +
                          ' (Important do not use any white color. Make sure share type title must be adding space if it is multiple words.)',
                      });

                      if (response.success) {
                        setAiGeneratedType(response.data);
                        // Refresh AI limit status after generation
                        await fetchAiLimitStatus();
                      } else {
                        throw new Error(response.message || 'Failed to generate AI share type');
                      }
                    } catch (error) {
                      console.error('AI generation error:', error);
                      // Fallback to mock response if API fails
                      await new Promise((resolve) => setTimeout(resolve, 1500));
                      const mockResponse = {
                        title: aiPrompt.trim(),
                        icon: '🎮',
                        bgGradient: 'from-blue-50 via-green-50 to-yellow-50',
                        cardGradient: 'from-blue-400 to-green-500',
                        accentColor: 'green-500',
                        textColor: 'green-600',
                        particles: ['⭐', '🎓', '🎮'],
                      };
                      setAiGeneratedType(mockResponse);
                      // Refresh AI limit status even for mock response
                      await fetchAiLimitStatus();
                    } finally {
                      setIsGeneratingAi(false);
                    }
                  }}
                  disabled={
                    !aiPrompt.trim() || isGeneratingAi || aiLimitStatus?.remainingCount === 0
                  }
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    !aiPrompt.trim() || isGeneratingAi || aiLimitStatus?.remainingCount === 0
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                  }`}
                >
                  {isGeneratingAi ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin mx-1 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  ) : (
                    // <img src={AiEditingIcon} />
                    <HugeiconsIcon icon={AiEditingIcon} />
                    // 'Generate'
                  )}
                </button>
              </div>

              {/* AI Limit Status */}
              <div>
                {isLoadingAiLimit ? (
                  <div className="text-[10px] text-gray-500 text-center">
                    <span className="inline-flex">
                      {'Loading...'.split('').map((char, index) => (
                        <span
                          key={index}
                          className="animate-pulse"
                          style={{
                            animationDelay: `${index * 0.1}s`,
                            animationDuration: '1s',
                          }}
                        >
                          {char}
                        </span>
                      ))}
                    </span>
                  </div>
                ) : aiLimitStatus ? (
                  aiLimitStatus.resetTime && (
                    <div className="text-[10px] text-gray-500 text-center">
                      Resets: {new Date(aiLimitStatus.resetTime).toLocaleTimeString()}
                    </div>
                  )
                ) : null}
              </div>
            </div>

            {/* AI Preview */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">🎨 Preview</label>
              <div
                className={`relative p-8 rounded-2xl border-2 transition-all duration-500 backdrop-blur-sm overflow-hidden ${
                  aiGeneratedType
                    ? `bg-gradient-to-br ${aiGeneratedType.bgGradient} border-${aiGeneratedType.accentColor} shadow-xl`
                    : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 shadow-md'
                }`}
              >
                {/* Animated particles background */}
                {aiGeneratedType && (
                  <AnimatedParticles
                    particles={aiGeneratedType.particles}
                    theme={aiGeneratedType}
                  />
                )}

                {aiGeneratedType ? (
                  <div className="relative z-10">
                    {/* Main content area matching SharePage layout */}
                    <div className="text-center mb-6">
                      {/* Large animated icon */}
                      <div className="text-6xl mb-4 animate-pulse">{aiGeneratedType.icon}</div>

                      {/* Share type card preview */}
                      <div
                        className={`inline-block px-6 py-3 rounded-xl bg-gradient-to-r ${aiGeneratedType.cardGradient} text-white font-semibold text-lg shadow-lg transform hover:scale-105 transition-all duration-300 mb-4`}
                      >
                        {aiGeneratedType.title || 'AI Generated Type'}
                      </div>
                    </div>

                    {/* Preview only - no action button here */}
                    <div className="text-center">
                      <div className="text-xs text-gray-600 opacity-75">
                        Click "Save Type" below to add this to your collection
                      </div>
                      <div
                        className={`mt-2 text-[8px] text-${aiGeneratedType.textColor} font-medium mb-4`}
                      >
                        Created with AI
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative z-10 text-center text-gray-500">
                    {/* Empty state matching SharePage style */}
                    <div className="mb-4">
                      <div className="text-5xl mb-3 animate-pulse">🎨</div>
                      <div className="text-lg font-medium mb-2">
                        {isGeneratingAi ? (
                          <div className="flex items-center justify-center space-x-2">
                            {/* <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div> */}
                            <span>AI is creating your custom share type...</span>
                          </div>
                        ) : (
                          'Ready to create something amazing?'
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {!isGeneratingAi && (
                          <span>
                            Enter a description above to generate a custom share type with{' '}
                            <img src={AILogo} alt="AI" className="inline-block w-4 h-4 mr-2" />
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Loading animation when generating */}
                    {isGeneratingAi && (
                      <div className="flex justify-center space-x-1 mb-4">
                        {['🎨', '✨', '🤖'].map((emoji, index) => (
                          <span
                            key={`loading-emoji-${index}`}
                            className="text-2xl animate-bounce"
                            style={{ animationDelay: `${index * 0.2}s` }}
                          >
                            {emoji}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Note Input - Only show in normal mode */}
        {!isAiMode && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('share.note') || 'Note'}
              <span className="text-gray-500 font-normal ml-1">
                ({t('common.optional') || 'Optional'})
              </span>
            </label>
            <div className="relative">
              <textarea
                value={note}
                autoFocus={!isMobile}
                onChange={(e) => {
                  if (orderStatus === 'failed' || orderStatus === 'cancelled') return;
                  const newValue = e.target.value;
                  if (newValue.length <= 200) {
                    setNote(newValue);
                    // Clear previous validation error when user starts typing
                    if (noteValidationError) {
                      setNoteValidationError(null);
                      setDetectedProfanityWords([]);
                    }
                    // Start real-time validation with debounce
                    debouncedValidateNote(newValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (orderStatus === 'failed' || orderStatus === 'cancelled') {
                    e.preventDefault();
                    return;
                  }
                  // Prevent new lines
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                  // Check character limit
                  if (
                    note.length >= 200 &&
                    ![
                      'Backspace',
                      'Delete',
                      'ArrowLeft',
                      'ArrowRight',
                      'ArrowUp',
                      'ArrowDown',
                    ].includes(e.key)
                  ) {
                    e.preventDefault();
                  }
                }}
                disabled={orderStatus === 'failed' || orderStatus === 'cancelled'}
                placeholder={
                  orderStatus === 'failed' || orderStatus === 'cancelled'
                    ? t('share.disabledPlaceholder') ||
                      'Sharing is disabled for failed/cancelled orders'
                    : t('share.notePlaceholder') || 'Add a personal message...'
                }
                className={`w-full min-h-[76px] px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent resize-none outline-none ${
                  orderStatus === 'failed' || orderStatus === 'cancelled'
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : noteValidationError
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                }`}
                style={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  maxHeight: '76px',
                  overflowY: 'auto',
                  backgroundColor: 'transparent',
                  color: 'transparent',
                  caretColor: '#374151',
                }}
              />
              {/* Overlay for highlighting profanity words */}
              <div
                className="absolute inset-0 px-3 py-2 pointer-events-none overflow-hidden"
                style={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  maxHeight: '76px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{
                  __html: note ? highlightProfanityWords(note, detectedProfanityWords) : '',
                }}
              />
              {isValidatingNote && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
            <div className="flex justify-between items-center mt-1">
              <div className="text-xs">
                {noteValidationError && (
                  <div className="text-red-600">
                    <div>{noteValidationError}</div>
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-500">{note.length}/200</div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            key="back-to-types-button"
            onClick={() => {
              if (isAiMode) {
                setIsAiMode(false);
              } else {
                onClose();
              }
            }}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {isAiMode ? '← Back to Types' : 'Cancel'}
          </button>

          {/* Show Save Type button in AI mode */}
          {isAiMode && aiGeneratedType && (
            <button
              key="save-type-button"
              onClick={async () => {
                try {
                  // Call add AI share type service
                  await addAIShareType({
                    title: aiGeneratedType.title || aiPrompt,
                    icon: aiGeneratedType.icon,
                    bgGradient: aiGeneratedType.bgGradient,
                    cardGradient: aiGeneratedType.cardGradient,
                    accentColor: aiGeneratedType.accentColor,
                    textColor: aiGeneratedType.textColor,
                    particles: aiGeneratedType.particles,
                  } as AddAIShareTypeRequest);

                  console.log('Share type added successfully');
                  showToast('success', 'Share type added successfully');

                  // Refresh user share types
                  setIsLoadingUserTypes(true);
                  try {
                    const response = await getUserShareTypes({ limit: 20 });
                    if (response.success) {
                      setUserShareTypes(response.data);
                    }
                  } catch (error) {
                    console.error('Failed to fetch user share types:', error);
                  } finally {
                    setIsLoadingUserTypes(false);
                  }

                  // Set the AI generated type as selected and switch to normal mode
                  setSelectedType(aiGeneratedType.title || aiPrompt);
                  setIsAiMode(false);
                  setAiPrompt('');
                  setAiGeneratedType(null);
                } catch (error) {
                  console.error('Error adding share type:', error);
                  showToast('error', 'Failed to add share type');
                }
              }}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 transition-colors flex items-center justify-center space-x-2"
            >
              <span>✨</span>
              <span>Save Type</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </button>
          )}

          {/* Only show Generate Share Link button in normal mode */}
          {!isAiMode && (
            <button
              key="generate-share-button"
              onClick={handleGenerateShare}
              disabled={
                isGenerating ||
                !user ||
                isValidatingNote ||
                !!noteValidationError ||
                orderStatus === 'failed' ||
                orderStatus === 'cancelled'
              }
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                isGenerating ||
                !user ||
                isValidatingNote ||
                !!noteValidationError ||
                orderStatus === 'failed' ||
                orderStatus === 'cancelled'
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t('share.generating') || 'Generating...'}
                </div>
              ) : (
                t('share.generate') || 'Generate Link'
              )}
            </button>
          )}
        </div>

        {/* Login Required Message */}
        {!user && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-700">
              {t('share.loginRequired') || 'You must be logged in to share orders'}
            </p>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-4 text-center">
                {t('share.deleteConfirmTitle') || 'Delete Share Type'}
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                {t('share.deleteConfirmMessage') ||
                  'Are you sure you want to delete this share type? This action cannot be undone.'}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setTypeToDelete(null);
                    setDeletingTypeId(null);
                  }}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                  disabled={deletingTypeId === typeToDelete}
                >
                  {t('cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleDeleteShareType}
                  className="flex-1 py-3 px-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  disabled={deletingTypeId === typeToDelete}
                >
                  {deletingTypeId === typeToDelete ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>{t('deleting') || 'Deleting...'}</span>
                    </div>
                  ) : (
                    t('share.delete') || 'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Reminder Dialog */}
        {isReminderForAi && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-4 text-center">
                {t('share.aiReminderTitle') || 'Share Type Limit Reached'}
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                {t('share.aiReminderMessage') ||
                  'You can only create up to 5 custom share types. Please delete some existing types to create new ones.'}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => setIsReminderForAi(false)}
                  className="w-full py-3 px-6 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                >
                  {t('common.okay') || 'Okay'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ShareDialog;
